import { useState, useEffect } from 'react';
import { Row, Col, Button, Form } from 'react-bootstrap';
import { Tooltip } from '@mui/material';
import { DataMap } from '@grapecity/wijmo.grid';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useRestApi } from 'context/RestApiContext';
import { useMaster } from 'context/MasterContext';
import { useAuth } from 'context/AuthContext';
import { MessageModal } from 'components/modal/MessageModal';
import { formatDateTime } from 'utils/dateUtils';
import './TaskCard.css';
import ExpiryModal from 'views/ClientCards/ExpiryModal';
import TaskCardEditCheckList from './TaskCardEditCheckList';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { linkifyDescription } from 'utils/stringUtils';

const WEEKDAYS = ['月', '火', '水', '木', '金', '土', '日'];

// スケジュールのラベルを生成（RecurringTaskManageModal の getFrequencyLabel と同じ形式）
const getScheduleLabel = (schedule) => {
  if (!schedule) return '';
  if (schedule.FREQUENCY === 'weekly') {
    let label = `毎週${WEEKDAYS[schedule.DAY_OF_WEEK] || ''}曜`;
    if (schedule.END_DAY_OF_WEEK != null) {
      label += ` → ${WEEKDAYS[schedule.END_DAY_OF_WEEK]}曜`;
      if (schedule.END_TIME) label += ` ${schedule.END_TIME}`;
    }
    return label;
  }
  let label = `毎月${schedule.DAY_OF_MONTH || ''}日`;
  if (schedule.END_DAY_OF_MONTH != null) {
    label += ` → ${schedule.END_DAY_OF_MONTH}日`;
    if (schedule.END_TIME) label += ` ${schedule.END_TIME}`;
  }
  return label;
};

// カードの編集画面
function TaskCardEditModal(props) {
  const baseUrl = process.env.REACT_APP_REST_URL ?? '';
  const master = useMaster();
  const restApi = useRestApi(); // 既存前提
  const auth = useAuth();
  const currentUserId = auth.auth.user_id;

  // コードマッピング
  var staffMap = new DataMap([...master.staff, { id: 0, name: '' }], 'id', 'name');

  const [show, setShow] = useState(false);
  // 管理者権限か？
  const [isAdimin] = useState(auth.auth.kbn.some((x) => x === 'admin'));

  // カード情報
  const [card, setCard] = useState({}); // 既存前提
  // コメント一覧
  const [comments, setComments] = useState([]);
  // 追加コメント
  const [newComment, setNewComment] = useState('');
  // タイトルを編集中か？
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  // 説明を編集中か？
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  // 編集中のタイトル
  const [editedTitle, setEditedTitle] = useState('');
  // 編集中の説明
  const [editedDescription, setEditedDescription] = useState('');
  // 編集中のコメント
  const [editedComment, setEditedComment] = useState('');
  // 編集中のコメントID
  const [editCommentId, setEditCommentId] = useState(null);
  // 確認ダイアログ
  const [dialog, setDialog] = useState({});

  // チェックリスト（編集用）
  const [checklist, setChecklist] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [editingChecklistId, setEditingChecklistId] = useState(null);

  // 定期タスク設定用
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [schedule, setSchedule] = useState(null); // 現在のスケジュール情報
  const [scheduleFrequency, setScheduleFrequency] = useState('weekly');
  const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState(0);
  const [scheduleDayOfMonth, setScheduleDayOfMonth] = useState(1);
  const [scheduleEndDayOfWeek, setScheduleEndDayOfWeek] = useState('');
  const [scheduleEndDayOfMonth, setScheduleEndDayOfMonth] = useState('');
  const [scheduleEndTime, setScheduleEndTime] = useState('');
  const [scheduleInitialListId, setScheduleInitialListId] = useState('');
  const [scheduleLists, setScheduleLists] = useState([]);

  // カード情報を検索して取得
  const searchCard = () => {
    restApi.get(`/api/task/cards/${props?.params?.card.CARD_ID}`, (data) => {
      const card = data.card;
      setCard(card);
      setIsEditingTitle(false);
      setIsEditingDescription(false);
      setEditedTitle(card.TITLE);
      setEditedDescription(card.DESCRIPTION || '');
      setComments(data.comments);
      setChecklist(data.checklist || []);
      setSchedule(data.schedule || null);

      setShow(true);
    });
  };

  useEffect(() => {
    if (props?.params?.card) {
      searchCard();
      setEditCommentId('');
      setNewComment('');
      // チェックリスト編集中状態の初期化
      setNewItem('');
      setEditingChecklistId(null);
    }
  }, [props.params]);

  // 追加コメントの保存ボタン押下
  const handleAddComment = () => {
    if (newComment.trim()) {
      restApi.post(`/api/task/cards/${card.CARD_ID}/comments`, { CONTENT: newComment }, (data) => {
        setComments([data, ...comments]);
        setNewComment('');
      });
    }
  };

  // 追加コメントの変更イベント
  const handleCommentChange = (e) => {
    setNewComment(e.target.value);
  };

  // 閉じるボタンのクリック処理
  const handleClose = () => {
    props.loadCards();
    setShow(false);
  };

  // タイトルのクリック処理
  const handleTitleClick = () => {
    setIsEditingTitle(true);
  };

  // 説明のクリック処理
  const handleDescriptionClick = () => {
    setIsEditingDescription(true);
  };

  // タイトルの変更イベント
  const handleTitleChange = (e) => {
    setEditedTitle(e.target.value);
  };

  // 説明の変更イベント
  const handleDescriptionChange = (e) => {
    setEditedDescription(e.target.value);
  };

  // タイトルの確定処理
  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    restApi.post(`/api/task/cards/${card.CARD_ID}`, { TITLE: editedTitle }, (_) => {
      setCard({ ...card, TITLE: editedTitle });
    });
  };

  // 説明の保存ボタン押下
  const handleSaveDescription = () => {
    restApi.post(`/api/task/cards/${card.CARD_ID}`, { DESCRIPTION: editedDescription }, (_) => {
      setCard({ ...card, DESCRIPTION: editedDescription });
      setIsEditingDescription(false);
    });
  };

  // 説明の編集キャンセルボタン押下
  const handleCancelDescription = () => {
    setIsEditingDescription(false);
    setEditedDescription(card.DESCRIPTION);
  };

  // タイトルのキーダウンイベント
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (isEditingTitle) {
        handleTitleBlur();
      }
    }
  };

  // タイトル左の完了トグル
  const handleClickTitleComplete = () => {
    const next = card.COMPLETE_FLG ? 0 : 1;
    restApi.post(`/api/task/cards/${card.CARD_ID}`, { COMPLETE_FLG: next }, (_) => {
      setCard({ ...card, COMPLETE_FLG: next });
    });
  };

  // コメントの編集リンクの選択
  const handleEditComment = (comment) => {
    setEditCommentId(comment.CARD_COMMENT_ID);
    setEditedComment(comment.CONTENT);
  };

  // コメントの内容変更
  const handleEditCommentChange = (e) => {
    setEditedComment(e.target.value);
  };

  // コメントの保存ボタン押下
  const handleSaveEditedComment = (commentId) => {
    restApi.post(
      `/api/task/cards/${card.CARD_ID}/comments/${commentId}`,
      { CONTENT: editedComment },
      (_) => {
        const updatedComments = comments.map((comment) =>
          comment.CARD_COMMENT_ID === commentId ? { ...comment, CONTENT: editedComment } : comment
        );
        setComments(updatedComments);
        setEditCommentId(null);
        setEditedComment('');
      }
    );
  };

  // コメントの削除リンク押下
  const handleDeleteComment = (commentId) => {
    setDialog({
      type: 'confirm',
      message: `削除します。よろしいですか？`,
      okCallBack: () => {
        restApi.doDelete(`/api/task/cards/${card.CARD_ID}/comments/${commentId}`, (_) => {
          setComments(comments.filter((x) => x.CARD_COMMENT_ID !== commentId));
        });
      },
    });
  };

  // グッドアイコンのクリック処理
  const handleGoodIcon = (commentId) => {
    restApi.post(`/api/task/cards/${card.CARD_ID}/comments/${commentId}/good`, {}, (data) => {
      let newComments = comments.map((x) =>
        x.CARD_COMMENT_ID !== commentId ? x : { ...x, GOOD_USERS: data.GOOD_USERS }
      );
      setComments(newComments);
    });
  };

  // コメントのキャンセルボタン押下
  const handleCancelEditComment = () => {
    setEditCommentId(null);
  };

  // 期限モーダルパラメタ
  const [expiryModalParam, setExpiryModalParam] = useState({});

  // 期限を選択
  const hendleClickExpiry = () =>
    setExpiryModalParam({
      show: true,
      expiry: card.EXPIRY,
      expiryTime: card.EXPIRY_TIME,
      cardId: card.CARD_ID,
      saveAction: (exp) => {
        restApi.post(`/api/task/cards/${card.CARD_ID}`, { EXPIRY: exp }, (data) => {
          searchCard();
        });
      },
    });

  // カードをアーカイブ
  const handleClickArchive = () => {
    setDialog({
      type: 'confirm',
      message: `カードをアーカイブします。よろしいですか？`,
      okCallBack: () => {
        restApi.post(
          `/api/task/cards/${card.CARD_ID}`,
          { ARCHIVE_FLG: '1', ARCHIVE_DATE: formatDateTime() },
          (_) => {
            props.loadCards();
            setShow(false);
          }
        );
      },
    });
  };

  // 削除を選択
  const handleClickDelete = () => {
    setDialog({
      type: 'confirm',
      message: `カードを削除します。よろしいですか？（※元に戻すことはできません）`,
      okCallBack: () => {
        restApi.post(`/api/task/cards/${card.CARD_ID}`, { DEL_FLG: '1' }, (_) => {
          props.loadCards();
          setShow(false);
        });
      },
    });
  };

  // 期限の削除
  const handleClickDeleteExpiry = () => {
    restApi.post(`/api/task/cards/${card.CARD_ID}`, { EXPIRY: null, EXPIRY_TIME: null }, (_) => {
      searchCard();
    });
  };

  // 定期タスク設定
  const handleClickScheduleSetting = () => {
    // リスト一覧を取得
    restApi.get('/api/task/recurring-cards', (data) => {
      setScheduleLists(data.lists || []);
    });
    if (schedule) {
      setScheduleFrequency(schedule.FREQUENCY || 'weekly');
      setScheduleDayOfWeek(schedule.DAY_OF_WEEK ?? 0);
      setScheduleDayOfMonth(schedule.DAY_OF_MONTH ?? 1);
      setScheduleEndDayOfWeek(schedule.END_DAY_OF_WEEK ?? '');
      setScheduleEndDayOfMonth(schedule.END_DAY_OF_MONTH ?? '');
      setScheduleEndTime(schedule.END_TIME || '');
      setScheduleInitialListId(schedule.INITIAL_LIST_ID ?? card.LIST_ID);
    } else {
      setScheduleFrequency('weekly');
      setScheduleDayOfWeek(0);
      setScheduleDayOfMonth(1);
      setScheduleEndDayOfWeek('');
      setScheduleEndDayOfMonth('');
      setScheduleEndTime('17:00');
      setScheduleInitialListId(card.LIST_ID);
    }
    setScheduleDialogOpen(true);
  };

  const handleConfirmSchedule = () => {
    const hasEndWeek = scheduleFrequency === 'weekly' && scheduleEndDayOfWeek !== '';
    const hasEndMonth = scheduleFrequency === 'monthly' && scheduleEndDayOfMonth !== '';
    const payload = {
      FREQUENCY: scheduleFrequency,
      DAY_OF_WEEK: scheduleFrequency === 'weekly' ? scheduleDayOfWeek : null,
      DAY_OF_MONTH: scheduleFrequency === 'monthly' ? scheduleDayOfMonth : null,
      END_DAY_OF_WEEK: hasEndWeek ? Number(scheduleEndDayOfWeek) : null,
      END_DAY_OF_MONTH: hasEndMonth ? Number(scheduleEndDayOfMonth) : null,
      END_TIME: (hasEndWeek || hasEndMonth) ? scheduleEndTime : null,
      INITIAL_LIST_ID: scheduleInitialListId,
    };
    restApi.post(`/api/task/cards/${card.CARD_ID}/schedule`, payload, () => {
      setScheduleDialogOpen(false);
      searchCard();
      setDialog({ type: 'info', message: '定期タスク設定を保存しました。' });
    });
  };

  const handleRemoveSchedule = () => {
    setDialog({
      type: 'confirm',
      message: '定期タスク設定を解除します。よろしいですか？',
      okCallBack: () => {
        restApi.doDelete(`/api/task/cards/${card.CARD_ID}/schedule`, () => {
          setScheduleDialogOpen(false);
          searchCard();
        });
      },
    });
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '1280px',
              backgroundColor: '#f1f2f4',
              marginTop: '20px',
            },
          },
        }}
        open={show}
        onClose={(e, reason) => {
          if (reason === 'backdropClick') {
            handleClose();
          }
        }}
      >
        <DialogTitle className="p-3">
          {/* タイトル行（左に完了トグルアイコン） */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingRight: '40px' }}>
            {card.COMPLETE_FLG == 0 ? (
              <RadioButtonUncheckedIcon
                sx={{ color: '#616368', cursor: 'pointer' }}
                fontSize="small"
                onClick={handleClickTitleComplete}
              />
            ) : (
              <CheckCircleIcon
                sx={{ color: '#30936a', cursor: 'pointer' }}
                fontSize="small"
                onClick={handleClickTitleComplete}
              />
            )}
            {isEditingTitle ? (
              <Form.Control
                type="text"
                value={editedTitle}
                onChange={handleTitleChange}
                onBlur={handleTitleBlur}
                onKeyDown={handleKeyDown}
                autoFocus
                style={{ width: '600px' }}
              />
            ) : (
              <span className="editable-title" onClick={handleTitleClick}>
                {card.TITLE}
              </span>
            )}
          </div>

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="px-3 py-0">
          <div style={{ display: 'flex' }}>
            <div style={{ width: '80%', flex: 1 }}>
              {schedule && (
                <div style={{ marginBottom: '4px' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '2px',
                    backgroundColor: '#e3f2fd', color: '#1976d2', borderRadius: '8px',
                    padding: '1px 6px', fontSize: '10pt', fontWeight: 'bold',
                  }}>
                    <AutorenewIcon style={{ fontSize: '14px' }} />
                    {getScheduleLabel(schedule)}
                  </span>
                </div>
              )}
              {card.EXPIRY && (
                <>
                  <div className="description-label">
                    <i className="material-symbols-rounded">date_range</i>
                    <b>期限</b>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} className="mb-2">
                    <div className="expiry_label mb-0" onClick={hendleClickExpiry}>
                      <span>
                        {card.EXPIRY ? card.EXPIRY : '期限なし'}
                        {card.EXPIRY_TIME && card.EXPIRY_TIME != '00:00' && ` ${card.EXPIRY_TIME}`}
                      </span>
                      <ExpandMoreIcon style={{ marginLeft: 'auto' }} />
                    </div>
                    <Tooltip title="期限を削除">
                      <Button
                        variant="light"
                        size="sm"
                        className="d-flex align-items-center"
                        style={{ padding: '4px 8px', backgroundColor: '#e9ecef', border: 'none', color: '#44546f' }}
                        onClick={handleClickDeleteExpiry}
                      >
                        <CloseIcon fontSize="small" style={{ marginRight: '4px' }} />
                        <span style={{ fontSize: '12px', fontWeight: 'bold' }}>削除</span>
                      </Button>
                    </Tooltip>
                  </div>
                </>
              )}
              {/* 説明 */}
              <div className="description-label mt-2">
                <i className="material-symbols-rounded">format_align_left</i>
                <b>説明</b>
              </div>
              {isEditingDescription ? (
                <>
                  <Form.Control
                    as="textarea"
                    placeholder="説明を入力してください。"
                    rows={5}
                    value={editedDescription}
                    onChange={handleDescriptionChange}
                    autoFocus
                  />
                  <div className="mt-2">
                    <Button
                      className="btn-fill"
                      variant="primary"
                      size="sm"
                      onClick={handleSaveDescription}
                    >
                      保存
                    </Button>
                    <Button
                      className="btn-fill ml-2"
                      variant="secondary"
                      size="sm"
                      onClick={handleCancelDescription}
                    >
                      キャンセル
                    </Button>
                  </div>
                </>
              ) : (
                <span
                  className="editable-description"
                  onClick={handleDescriptionClick}
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {linkifyDescription(card.DESCRIPTION, '説明を入力してください。')}
                </span>
              )}
              {/* チェックリスト */}
              <div className="description-label mt-2">
                <i className="material-symbols-rounded">playlist_add_check</i>
                <b>チェックリスト</b>
              </div>
              <TaskCardEditCheckList
                cardId={card.CARD_ID}
                checklist={checklist}
                onChange={setChecklist}
                suggestions={[]}
              />
              {/* コメント */}
              <div className="description-label mt-2">
                <i className="material-symbols-rounded">format_list_bulleted</i>
                <b>コメント</b>
              </div>
              <Form.Control
                as="textarea"
                rows={6}
                value={newComment}
                onChange={handleCommentChange}
                placeholder="コメントを入力してください"
              />
              <div className="mt-2 mb-2">
                <Button
                  className="btn-fill"
                  variant="primary"
                  size="sm"
                  disabled={!newComment}
                  onClick={handleAddComment}
                >
                  保存
                </Button>
              </div>
              <ul className="comments-list">
                {comments.map((comment, index) => (
                  <span key={index}>
                    <span style={{ fontSize: '11pt' }}>
                      <b>{comment.TAN_NAME}</b>さんがコメント
                    </span>
                    {editCommentId === comment.CARD_COMMENT_ID ? (
                      <>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={editedComment}
                          onChange={handleEditCommentChange}
                          className="mb-2"
                        />
                        <Button
                          className="btn-fill"
                          variant="primary"
                          size="sm"
                          onClick={() => handleSaveEditedComment(comment.CARD_COMMENT_ID)}
                        >
                          保存
                        </Button>
                        <Button
                          className="btn-fill ml-2"
                          variant="secondary"
                          size="sm"
                          onClick={handleCancelEditComment}
                        >
                          キャンセル
                        </Button>
                        <br />
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: '10pt' }} className="pl-2">
                          &nbsp;{comment.INS_DATE}&nbsp;{comment.INS_TIME}
                        </span>
                        <li key={index} className="comment-item">
                          {linkifyDescription(comment.CONTENT, '')}
                        </li>
                        <div
                          className="mt-1 ml-1 mb-3"
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          <a
                            href="#"
                            style={{
                              fontSize: '10pt',
                              color: '#44546f',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                            }}
                            onClick={() => handleGoodIcon(comment.CARD_COMMENT_ID)}
                          >
                            <div
                              className={
                                comment.GOOD_USERS.includes(currentUserId)
                                  ? 'goodicon_mine'
                                  : 'goodicon'
                              }
                            >
                              {comment.GOOD_USERS?.length >= 1 ? (
                                <Tooltip
                                  title={comment.GOOD_USERS?.map((x) =>
                                    staffMap.getDisplayValue(x)
                                  ).join(', ')}
                                >
                                  <ThumbUpAltRoundedIcon
                                    style={{ color: '#fcc625' }}
                                    fontSize="small"
                                  />
                                  <span
                                    style={{
                                      marginLeft: '8px',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {comment.GOOD_USERS?.length}
                                  </span>
                                </Tooltip>
                              ) : (
                                <ThumbUpAltOutlinedIcon fontSize="small" />
                              )}
                            </div>
                          </a>

                          {(isAdimin || currentUserId == comment.TAN_CD) && (
                            <>
                              <a
                                href="#"
                                style={{
                                  fontSize: '10pt',
                                  color: '#44546f',
                                  textDecoration: 'none',
                                  marginLeft: '8px',
                                }}
                                onClick={() => handleEditComment(comment)}
                              >
                                <u>編集</u>
                              </a>
                              ・
                              <a
                                href="#"
                                style={{
                                  fontSize: '10pt',
                                  color: '#44546f',
                                  textDecoration: 'none',
                                  marginLeft: '8px',
                                }}
                                onClick={() => handleDeleteComment(comment.CARD_COMMENT_ID)}
                              >
                                <u>削除</u>
                              </a>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </span>
                ))}
              </ul>
            </div>
            <div
              style={{
                width: '20%',
                padding: '0px',
                marginLeft: '10px',
              }}
            >
              <div className="description-label">
                <b>操作</b>
              </div>
              <div className="menu-item" onClick={hendleClickExpiry}>
                <AccessTimeIcon></AccessTimeIcon>
                <span
                  style={{
                    marginLeft: '6px',
                  }}
                >
                  期限を編集
                </span>
              </div>
              <div className="menu-item mt-2" onClick={handleClickArchive}>
                <Inventory2OutlinedIcon></Inventory2OutlinedIcon>
                <span
                  style={{
                    marginLeft: '6px',
                  }}
                >
                  アーカイブ
                </span>
              </div>

              <div className="menu-item mt-2" onClick={handleClickDelete}>
                <DeleteForeverOutlinedIcon style={{ fontSize: 30, marginLeft: '-2px' }} />
                <span
                  style={{
                    marginLeft: '6px',
                  }}
                >
                  削除
                </span>
              </div>

              <div className="menu-item mt-2" onClick={handleClickScheduleSetting}
                style={{ borderTop: '1px solid #ccc', paddingTop: '12px', marginTop: '12px' }}
              >
                <AutorenewIcon style={{ color: schedule ? '#1976d2' : '#666' }} />
                <span
                  style={{
                    marginLeft: '6px',
                  }}
                >
                  定期タスク設定
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* メッセージダイアログ */}
      <MessageModal dialog={dialog}></MessageModal>
      {/* 期限入力 */}
      <ExpiryModal params={expiryModalParam}></ExpiryModal>
      {/* 定期タスク設定ダイアログ */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)}>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AutorenewIcon style={{ color: '#1976d2' }} />
          定期タスク設定
        </DialogTitle>
        <DialogContent style={{ minWidth: '400px' }}>
          <div className="mb-2" style={{ marginTop: '8px' }}>
            <label style={{ fontWeight: 'bold', fontSize: '10pt', display: 'block', marginBottom: '4px' }}>初期配置リスト</label>
            <select
              className="form-control form-control-sm"
              value={scheduleInitialListId}
              onChange={(e) => setScheduleInitialListId(Number(e.target.value))}
            >
              {scheduleLists.map((l) => (
                <option key={l.LIST_ID} value={l.LIST_ID}>{l.LIST_NAME}</option>
              ))}
            </select>
          </div>
          <div className="mb-2 mt-2">
            <label style={{ fontWeight: 'bold', fontSize: '10pt', display: 'block', marginBottom: '4px' }}>頻度</label>
            <select
              className="form-control form-control-sm"
              value={scheduleFrequency}
              onChange={(e) => {
                setScheduleFrequency(e.target.value);
                setScheduleEndDayOfWeek('');
                setScheduleEndDayOfMonth('');
                setScheduleEndTime('');
              }}
            >
              <option value="weekly">毎週</option>
              <option value="monthly">毎月</option>
            </select>
          </div>
          {scheduleFrequency === 'weekly' ? (
            <div className="mb-2">
              <label style={{ fontWeight: 'bold', fontSize: '10pt', display: 'block', marginBottom: '4px' }}>開始曜日</label>
              <select
                className="form-control form-control-sm"
                value={scheduleDayOfWeek}
                onChange={(e) => setScheduleDayOfWeek(Number(e.target.value))}
              >
                {['月', '火', '水', '木', '金', '土', '日'].map((d, i) => (
                  <option key={i} value={i}>{d}曜日</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="mb-2">
              <label style={{ fontWeight: 'bold', fontSize: '10pt', display: 'block', marginBottom: '4px' }}>開始日</label>
              <select
                className="form-control form-control-sm"
                value={scheduleDayOfMonth}
                onChange={(e) => setScheduleDayOfMonth(Number(e.target.value))}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}日</option>
                ))}
              </select>
            </div>
          )}
          {scheduleFrequency === 'weekly' ? (
            <div className="mb-2">
              <label style={{ fontWeight: 'bold', fontSize: '10pt', display: 'block', marginBottom: '4px' }}>終了曜日</label>
              <select
                className="form-control form-control-sm"
                value={scheduleEndDayOfWeek}
                onChange={(e) => {
                  setScheduleEndDayOfWeek(e.target.value);
                  if (e.target.value === '') setScheduleEndTime('');
                }}
              >
                <option value="">なし</option>
                {['月', '火', '水', '木', '金', '土', '日'].map((d, i) => (
                  <option key={i} value={i}>{d}曜日</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="mb-2">
              <label style={{ fontWeight: 'bold', fontSize: '10pt', display: 'block', marginBottom: '4px' }}>終了日</label>
              <select
                className="form-control form-control-sm"
                value={scheduleEndDayOfMonth}
                onChange={(e) => {
                  setScheduleEndDayOfMonth(e.target.value);
                  if (e.target.value === '') setScheduleEndTime('');
                }}
              >
                <option value="">なし</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}日</option>
                ))}
              </select>
            </div>
          )}
          {(scheduleEndDayOfWeek !== '' || scheduleEndDayOfMonth !== '') && (
            <div className="mb-2">
              <label style={{ fontWeight: 'bold', fontSize: '10pt', display: 'block', marginBottom: '4px' }}>終了時刻</label>
              <select
                className="form-control form-control-sm"
                value={scheduleEndTime}
                onChange={(e) => setScheduleEndTime(e.target.value)}
              >
                <option value="">なし</option>
                {Array.from({ length: 8 }, (_, i) => i + 10)
                  .map((i) => `${i.toString().padStart(2, '0')}:00`)
                  .map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
              </select>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
            <div>
              {schedule && (
                <Button variant="outline-danger" size="sm" onClick={handleRemoveSchedule}>
                  設定を解除
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="secondary" size="sm" onClick={() => setScheduleDialogOpen(false)}>
                キャンセル
              </Button>
              <Button variant="primary" size="sm" className="btn-fill" onClick={handleConfirmSchedule}>
                保存
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TaskCardEditModal;
